import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { formatText } from '~/utilities/helpers';
import { h1, h2, h3, h4, a } from '~/styles/typography';

const fontSizes = {
    h1,
    h2,
    h3
};

const getHeadingStyles = ({
    theme,
    fontSize,
    textAlign,
    textColor,
    headingLevel
}) => {
    let headingStyle = h4;
    switch (headingLevel) {
        case 'h1':
            headingStyle = h1;
            break;
        case 'h2':
            headingStyle = h2;
            break;
        case 'h3':
            headingStyle = h3;
            break;
        case 'h4':
            headingStyle = h4;
            break;
        default:
            headingStyle = h4;
    }

    if (fontSize) {
        headingStyle = fontSizes[fontSize];
    }

    let overrideStyles = ``;
    if (textAlign) {
        overrideStyles += `
        text-align: ${textAlign};
    `;
    } else {
        overrideStyles += `
        text-align: left;
    `;
    }

    if (textColor) {
        overrideStyles += `
        color: ${theme.colors.text[textColor]};
    `;
    }

    return css`
        ${headingStyle({ theme })}
        ${overrideStyles}
        
        margin-bottom: 15px;

        a {
            ${a({ theme })};
        }
    `;
};

const StyledH1 = styled.h1`
    ${({ theme, fontSize, textAlign, textColor }) => getHeadingStyles({ theme, fontSize, textAlign, textColor, headingLevel: 'h1' })};
}`;

const StyledH2 = styled.h2`
  ${({ theme, fontSize, textAlign, textColor }) => getHeadingStyles({ theme, fontSize, textAlign, textColor, headingLevel: 'h2' })};
}`;

const StyledH3 = styled.h3`
  ${({ theme, fontSize, textAlign, textColor }) => getHeadingStyles({ theme, fontSize, textAlign, textColor, headingLevel: 'h3' })};
}`;

const StyledH4 = styled.h4`
  ${({ theme, fontSize, textAlign, textColor }) => getHeadingStyles({ theme, fontSize, textAlign, textColor, headingLevel: 'h4' })};
}`;

const getHeading = (level) => {
    switch (level) {
        case 1:
            return StyledH1;
        case 2:
            return StyledH2;
        case 3:
            return StyledH3;
        case 4:
            return StyledH4;
        default:
            return StyledH4;
    }
};

const Heading = ({
    anchor = '',
    textAlign = '',
    content,
    level,
    fontSize = '',
    textColor = '',
    className = ''
}) => {
    const HeadingTag = getHeading(level);

    return (
        <HeadingTag
            {...(anchor ? { id: anchor } : {})}
            {...{ textAlign, fontSize, textColor, className }}
        >
            {formatText(content)}
        </HeadingTag>
    );
};

Heading.propTypes = {
    anchor: PropTypes.string,
    textAlign: PropTypes.string,
    content: PropTypes.string.isRequired,
    level: PropTypes.number.isRequired,
    fontSize: PropTypes.string,
    textColor: PropTypes.string,
    className: PropTypes.string
};

export default Heading;
